import { escapeHtml } from '@colplat/ui-web';
import { convertTotals, nonZeroCurrencies, type FxRates } from './webledgerReportsData.js';

/**
 * The totals bar's currency mode — the control and the sentence it produces.
 *
 * Shared because the interesting part is not the arithmetic but what happens
 * when the arithmetic cannot be done. A converted total that quietly leaves
 * out the currency it has no rate for looks complete and is wrong, and it is
 * wrong by an amount nobody can see. That warning, and the one for rates that
 * could not be refreshed, have to say the same thing on every page that
 * offers the conversion — so there is one of them.
 *
 * `chip` is passed in rather than imported: each page already has its own,
 * bound to its own currency metadata.
 */

/** Renders one amount in one currency. */
export type ChipFn = (amount: string, code: string) => string;

/**
 * Fill a mode select with "By currency" plus whatever the server offers to
 * convert into, and select `remembered` if it is still one of them.
 *
 * Hidden when nothing is offered: a control with one option is furniture.
 */
export function fillTotalModes(
  select: HTMLSelectElement | null, fx: FxRates | undefined, remembered: string,
): void {
  if (!select) return;
  const offered = fx?.totalsCurrencies ?? [];
  select.innerHTML = '<option value="by">By currency</option>'
    + offered.map((c) => `<option value="${escapeHtml(c.code)}">In ${escapeHtml(c.code)}</option>`).join('');
  // A remembered currency that is no longer offered falls back rather than
  // leaving the select showing a value it does not have.
  select.value = [...select.options].some((o) => o.value === remembered) ? remembered : 'by';
  select.hidden = offered.length === 0;
}

/**
 * The totals themselves: a chip per currency, or one converted chip carrying
 * whatever could not be included.
 */
export function totalsHtml(
  totals: Record<string, string>, mode: string, fx: FxRates | undefined, chip: ChipFn,
): string {
  const codes = nonZeroCurrencies(totals);
  if (codes.length === 0) return '<span class="text-secondary">–</span>';
  if (mode === 'by') return codes.map((c) => chip(totals[c]!, c)).join('');

  const { amount, missing } = convertTotals(totals, mode, fx?.rates ?? {});
  const warning = missing.length > 0
    ? ` <span class="wl-fx-warn" title="No rate for ${escapeHtml(missing.join(', '))} — not included">⚠</span>`
    : '';
  const stale = fx?.stale
    ? ' <span class="wl-fx-warn" title="Rates could not be refreshed today">rates may be out of date</span>'
    : '';
  return chip(String(amount), mode) + warning + stale;
}
